import _ from 'lodash';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCSVReader } from 'react-papaparse';

import { useGoogleMyBusinessLocationsQuery } from '@adflow/frontend/feat-google-my-business';
import { MultiselectWithControl } from '@adflow/frontend/ui-form';
import { createWarningToast } from '@adflow/frontend/ui-toasts';
import {
	type AddGMBTemplateDto,
	getGMBLocationName,
	type ViewGMBLocationDto,
} from '@adflow/shared/data-google-my-business';

export function GoogleAdsLocationSelector() {
	const { t } = useTranslation('google-ads');

	const { data: gmbData, isLoading: isLoadingGmb } =
		useGoogleMyBusinessLocationsQuery();

	const { CSVReader } = useCSVReader();

	const formMethods = useFormContext<AddGMBTemplateDto>();

	const updateLocations = useCallback(
		(locationAvbIds: string[]) => {
			if (_.isEmpty(locationAvbIds)) return;

			const currentLocations = formMethods.getValues('locations') || [];
			const rejectedLocations: string[] = [];

			// Find all matching locations from gmbData based on avbIds
			const newLocations = _.chain(locationAvbIds)
				.flatMap((avbId) => {
					const lowerId = avbId.toLocaleLowerCase();
					const matchingLocations = _.filter(
						gmbData,
						(location) => location.avbId.toLocaleLowerCase() === lowerId
					);

					if (_.isEmpty(matchingLocations)) {
						rejectedLocations.push(avbId);
						return [];
					}

					return matchingLocations;
				})
				.value();

			if (!_.isEmpty(rejectedLocations)) {
				createWarningToast(
					t('facebook:adAccountMissingWarning') +
						(rejectedLocations.length === 1
							? rejectedLocations[0]
							: rejectedLocations.join(', '))
				);
			}

			// Combine current and new locations, ensuring uniqueness by avbId and googleId
			formMethods.setValue(
				'locations',
				_.uniqBy(
					[...currentLocations, ...newLocations],
					(location) => `${location.avbId}_${location.googleId}`
				),
				{ shouldDirty: true }
			);
		},
		[t, gmbData, formMethods]
	);

	const handleCSVUpload = (results: { data: string[][] }) => {
		const locationIds = _.flatMap(results.data, (row) =>
			_.chain(row)
				.map((item) => _.trim(item))
				.filter((item) => item !== '')
				.value()
		);
		if (locationIds.length > 0) {
			updateLocations(locationIds);
		}
	};

	return (
		<MultiselectWithControl<ViewGMBLocationDto>
			name="locations"
			label="Locations"
			options={gmbData ?? []}
			getOptionKey={(_index, option) => option.googleId}
			isOptionEqualToValue={(option, value) =>
				option.googleId === value.googleId
			}
			getOptionLabel={getGMBLocationName}
			outerStyle={{ gridColumn: 'span 8 / span 8' }}
			maxListSize={1000}
			helperText={
				<CSVReader onUploadAccepted={handleCSVUpload}>
					{({
						getRootProps,
					}: {
						getRootProps: () => Record<string, unknown>;
					}) => (
						<button type="button" {...getRootProps()}>
							{t('facebook:adAccountCSV')}
						</button>
					)}
				</CSVReader>
			}
			slotProps={{
				input: {
					onPaste: (e) => {
						const pastedText = e.clipboardData.getData('text');
						const adAccountIds = pastedText
							.split(/[,\n]/u)
							.map((id) => id.trim())
							.filter(Boolean);
						if (adAccountIds.length > 0) {
							updateLocations(adAccountIds);
						}
						e.preventDefault();
						return false;
					},
				},
			}}
			isLoading={isLoadingGmb}
			reactWindow
		/>
	);
}
